import {
  Attainments,
  Attendance,
  BusinessCardScanner,
  Clubs,
  Events,
  MyCardHolder,
  PaymentGateway,
} from '@ice-web-app/assets';
import { DsButton, DsCheckbox, DsIcon, DsModal, DsTabs, DsTextField, DsTypography } from '@ice-web-app/designSystem';
import { useTranslation } from '@ice-web-app/shared-hooks';
import { Divider, Stack, useMediaQuery, useTheme } from '@mui/material';
import { Dispatch, FC, ReactElement, SetStateAction, useEffect, useState } from 'react';
import { toast } from 'react-toastify';

interface Props {
  open: boolean;
  setOpen: Dispatch<SetStateAction<boolean>>;
  quickLink: {
    key: string;
    icon: ReactElement;
    text: string;
  }[];
  setQuickLink: Dispatch<
    SetStateAction<
      {
        key: string;
        icon: ReactElement;
        text: string;
      }[]
    >
  >;
}

function areEqualArrays(arr1, arr2) {
  return new Set(arr1).size === new Set(arr2).size && [...new Set(arr1)].every((item) => arr2.includes(item));
}

const AddQuickLinkModal: FC<Props> = ({ open, setOpen, quickLink, setQuickLink }) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMedium = useMediaQuery(`(min-width:${theme.breakpoints.values.md}px)`);
  const [tab, setTab] = useState<'SMART' | 'SAT_V1' | 'GEIC' | 'CONTENT' | 'COMMUNICATION' | 'WORKFLOW' | 'M.I'>(
    'SMART',
  );
  const [checkedKey, setCheckedKey] =
    useState<
      {
        key: string;
        icon: ReactElement;
        text: string;
      }[]
    >(quickLink);

  const allLinks = [
    { key: 'ATTENDANCE', icon: <Attendance />, text: t('ATTENDANCE') },
    { key: 'BUSINESS_CARD_SCANNER', icon: <BusinessCardScanner />, text: t('BUSINESS_CARD_SCANNER') },
    { key: 'CLUBS', icon: <Clubs />, text: t('CLUBS') },
    { key: 'EVENTS', icon: <Events />, text: t('EVENTS') },
    { key: 'MY_CARD_HOLDER', icon: <MyCardHolder />, text: t('MY_LABEL', { label: t('CARD_HOLDER') }) },
    { key: 'EVENT_GATEWAY', icon: <Attainments />, text: t('EVENT_GATEWAY') },
    { key: 'PAYMENTS', icon: <PaymentGateway />, text: t('PAYMENTS') },
    { key: 'ATTAINMENTS', icon: <Attainments />, text: t('ATTAINMENTS') },
  ];

  const toggleCheck = (link: {
    key: string;
    icon: ReactElement;
    text: string;
  }) => {
    if (checkedKey.find(({ key }) => key === link.key)) {
      const newState = checkedKey.filter(({ key }) => key !== link.key);
      setCheckedKey(newState);
    } else {
      setCheckedKey([...checkedKey, link]);
    }
  };

  useEffect(() => {
    setCheckedKey(quickLink);
  }, [open]);

  return (
    <DsModal
      open={open}
      onClose={() => setOpen(false)}
      size="md"
      header={t('ADD_QUICK_LINKS_DIFF_ZONE')}
      headerTypographyVariant="h5"
      content={
        <Stack gap={5}>
          <DsTabs
            type="flat"
            value={tab}
            onChange={(_, value) => setTab(value)}
            variant={isMedium ? 'fullWidth' : 'scrollable'}
            items={[
              { value: 'SMART', label: t('SMART') },
              { value: 'SAT_V1', label: t('SAT_V1') },
              { value: 'GEIC', label: t('GEIC') },
              { value: 'CONTENT', label: t('CONTENT') },
              { value: 'COMMUNICATION', label: t('COMMUNICATION') },
              { value: 'WORKFLOW', label: t('WORKFLOW') },
              { value: 'M.I', label: t('M.I') },
            ]}
          />
          <DsTextField prefixIcon={{ icon: <DsIcon icon="SEARCH" /> }} placeholder={t('SEARCH')} />
          <Stack divider={<Divider sx={{ my: 0 }} />}>
            {allLinks.map((link) => (
              <Stack
                key={link.key}
                direction="row"
                sx={{
                  height: 64,
                  cursor: 'pointer',
                  borderRadius: 2,
                  transition: 'all .2s',
                  '&:hover': {
                    bgcolor: ({ palette }) => palette.background.default,
                  },
                }}
                alignItems="center"
                gap={5.5}
                onClick={() => toggleCheck(link)}
              >
                <DsCheckbox checked={!!checkedKey.find(({ key }) => key === link.key)} />
                <DsTypography variant="body1_500">{link.text}</DsTypography>
              </Stack>
            ))}
          </Stack>
          <Stack direction="row-reverse" gap={4}>
            <DsButton
              size="medium"
              sx={{ width: 100, marginInlineStart: '0 !important' }}
              variant="contained"
              disabled={areEqualArrays(
                quickLink.map(({ key }) => key),
                checkedKey.map(({ key }) => key),
              )}
              onClick={() => {
                setQuickLink(checkedKey);
                setOpen(false);
                toast.success(t('QUICK_LINKS_CHANGE_SUCCESSFULLY'));
              }}
            >
              {t('CONFIRM')}
            </DsButton>
            <DsButton onClick={() => setOpen(false)} size="medium" sx={{ width: 100 }} variant="outlined">
              {t('CANCEL')}
            </DsButton>
          </Stack>
        </Stack>
      }
    />
  );
};
export default AddQuickLinkModal;
